import { useState } from "react";
import { motion } from "framer-motion";
import { Tag, Truck, Sparkles, Star, AlertTriangle } from "lucide-react";
import { getActiveProjectGraph } from "../../data/sampleGraph";
import { photoForSku } from "../../data/imagery";
import type { GraphNode } from "../../types/ontology";
import { cls } from "../../lib/cls";
import { alertsIndex } from "../../lib/alertsIndex";
import { formatMoney } from "../../lib/i18n/format";

interface CatalogGridProps {
  highlightIds?: Set<string>;
  onSelect?: (sku: GraphNode) => void;
}

function vendorLabel(vendorId: unknown): string {
  if (typeof vendorId !== "string") return "Vendor";
  const v = getActiveProjectGraph().nodes.find((n) => n.id === vendorId);
  return v?.label ?? "Vendor";
}

function gradientFor(id: string): string {
  const palette: [string, string][] = [
    ["#FCE6D2", "#E8B591"], ["#E6E2DC", "#C9BBA9"], ["#FCFBF8", "#F0EDE8"],
    ["#D6B7C5", "#B19BAB"], ["#B3D4BF", "#88B79A"], ["#A8C2DD", "#7A9BBC"],
  ];
  let h = 0;
  for (let i = 0; i < id.length; i += 1) h = (h * 31 + id.charCodeAt(i)) >>> 0;
  const pair = palette[h % palette.length] ?? palette[0];
  if (!pair) return "linear-gradient(135deg, #FEFDFB, #F0EDE8)";
  return `linear-gradient(135deg, ${pair[0]}, ${pair[1]})`;
}

function SkuPhoto({ label, id }: { label: string; id: string }): JSX.Element {
  const [failed, setFailed] = useState(false);
  if (failed) {
    return (
      <div
        className="absolute inset-0"
        style={{ background: gradientFor(id) }}
        aria-hidden
      />
    );
  }
  return (
    <img
      src={photoForSku(label)}
      alt={label}
      loading="lazy"
      onError={() => setFailed(true)}
      className="absolute inset-0 w-full h-full object-cover"
    />
  );
}

export function CatalogGrid({ highlightIds, onSelect }: CatalogGridProps): JSX.Element {
  const skus = getActiveProjectGraph().nodes.filter((n) => n.kind === "SKU");
  const alerts = alertsIndex();
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
      {skus.map((sku, i) => {
        const lead = Number(sku.attrs["leadDays"] ?? 0);
        const price = Number(sku.attrs["price"] ?? 0);
        const styleMatch = Number(sku.attrs["styleMatch"] ?? 0);
        const vendorId = sku.attrs["vendor"];
        const dataset = sku.attrs["source"];
        const highlighted = highlightIds?.has(sku.id) ?? false;
        const skuAlerts = alerts.bySkuId.get(sku.id) ?? [];
        return (
          <motion.button
            key={sku.id}
            type="button"
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.25, delay: i * 0.03 }}
            onClick={() => onSelect?.(sku)}
            className={cls(
              "panel text-left p-0 overflow-hidden group transition-all",
              skuAlerts.length > 0 ? "ring-1 ring-foyr/40" : "",
              highlighted ? "ring-2 ring-foyr/60 shadow-deep" : "hover:shadow-deep",
            )}
          >
            <div className="relative h-44 overflow-hidden">
              <SkuPhoto label={sku.label} id={sku.id} />
              <div className="absolute inset-0 bg-gradient-to-t from-ink-950/45 via-transparent to-transparent pointer-events-none" />
              {lead > 60 ? (
                <span className="absolute top-2 left-2 pill bg-foyr text-cream">
                  <Truck className="w-3 h-3" /> {lead}d lead
                </span>
              ) : null}
              {styleMatch >= 0.9 ? (
                <span className="absolute top-2 right-2 pill bg-cream text-ink-950">
                  <Sparkles className="w-3 h-3" /> {(styleMatch * 100).toFixed(0)}%
                </span>
              ) : null}
              {skuAlerts.length > 0 ? (
                <span className="absolute bottom-2 left-2 pill bg-foyr/95 text-cream backdrop-blur-md">
                  <AlertTriangle className="w-3 h-3" /> {skuAlerts.length} alert{skuAlerts.length > 1 ? "s" : ""}
                </span>
              ) : null}
            </div>
            <div className="p-3 space-y-1.5">
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm font-medium text-ink-950 truncate">{sku.label}</span>
                <span className="text-xs tabular text-ink-700 shrink-0">{formatMoney(price)}</span>
              </div>
              <div className="flex items-center justify-between text-[11px] text-ink-500">
                <span className="inline-flex items-center gap-1 truncate">
                  <Tag className="w-3 h-3" /> {vendorLabel(vendorId)}
                </span>
                <span className="inline-flex items-center gap-1 tabular">
                  <Star className="w-3 h-3 text-foyr" /> {(styleMatch * 100).toFixed(0)}
                </span>
              </div>
              {typeof dataset === "string" ? (
                <div className="text-[10px] font-mono text-ink-500/70 truncate">{dataset}</div>
              ) : null}
            </div>
          </motion.button>
        );
      })}
    </div>
  );
}
